// Copied verbatim from the client portal (helpdesk.matix.one, /home/helpdeskmatix/public_html/src/components/ImageEditor.js, 2026-10-12)
// so the agent's reply box attaches exactly like the portal's «تذكرة جديدة». Keep the two in step.
import React, { useState, useRef, useEffect, useCallback } from 'react';

const ImageEditor = ({ image, onSave, onCancel, language = 'en' }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [ctx, setCtx] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [tool, setTool] = useState('pen'); // pen, rectangle, circle, arrow, text, highlight, blur, crop
    const [color, setColor] = useState('#FF0000');
    const [brushSize, setBrushSize] = useState(3);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [textInput, setTextInput] = useState('');
    const [textPosition, setTextPosition] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
    const [cropStart, setCropStart] = useState(null);
    const [cropEnd, setCropEnd] = useState(null);
    const [isCropping, setIsCropping] = useState(false);

    const t = {
        ar: {
            pen: 'قلم',
            rectangle: 'مستطيل',
            circle: 'دائرة',
            arrow: 'سهم',
            text: 'نص',
            highlight: 'تمييز',
            blur: 'تعتيم',
            crop: 'قص',
            undo: 'تراجع',
            redo: 'إعادة',
            save: 'حفظ',
            cancel: 'إلغاء',
            color: 'اللون',
            size: 'الحجم',
            small: 'صغير',
            medium: 'متوسط',
            large: 'كبير',
            enterText: 'اكتب النص...',
            applyCrop: 'تطبيق القص',
            cancelCrop: 'إلغاء القص'
        },
        en: {
            pen: 'Pen',
            rectangle: 'Rectangle',
            circle: 'Circle',
            arrow: 'Arrow',
            text: 'Text',
            highlight: 'Highlight',
            blur: 'Blur',
            crop: 'Crop',
            undo: 'Undo',
            redo: 'Redo',
            save: 'Save',
            cancel: 'Cancel',
            color: 'Color',
            size: 'Size',
            small: 'Small',
            medium: 'Medium',
            large: 'Large',
            enterText: 'Enter text...',
            applyCrop: 'Apply Crop',
            cancelCrop: 'Cancel Crop'
        }
    }[language] || {};

    const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#000000', '#FFFFFF'];

    // Load image
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setOriginalImage(img);
            
            // Calculate canvas size to fit container while maintaining aspect ratio
            const maxWidth = Math.min(window.innerWidth * 0.8, 1200);
            const maxHeight = window.innerHeight * 0.6;
            
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth) {
                height = (height / width) * maxWidth;
                width = maxWidth;
            }
            if (height > maxHeight) {
                width = (width / height) * maxHeight;
                height = maxHeight;
            }
            
            setCanvasSize({ width: Math.round(width), height: Math.round(height) });
            setImageLoaded(true);
        };
        img.src = image;
    }, [image]);

    // Initialize canvas
    useEffect(() => {
        if (!imageLoaded || !canvasRef.current || !originalImage) return;
        
        const canvas = canvasRef.current;
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;
        
        const context = canvas.getContext('2d');
        context.drawImage(originalImage, 0, 0, canvasSize.width, canvasSize.height);
        setCtx(context);
        
        // Save initial state
        saveToHistory();
    }, [imageLoaded, canvasSize, originalImage]);

    // Save canvas state to history
    const saveToHistory = useCallback(() => {
        if (!canvasRef.current) return;
        
        const canvas = canvasRef.current;
        const imageData = canvas.toDataURL();
        
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(imageData);
            return newHistory.slice(-20); // Keep last 20 states
        });
        setHistoryIndex(prev => Math.min(prev + 1, 19));
    }, [historyIndex]);

    // Undo
    const handleUndo = () => {
        if (historyIndex <= 0) return;
        
        const newIndex = historyIndex - 1;
        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
            ctx.drawImage(img, 0, 0);
            setHistoryIndex(newIndex);
        };
        img.src = history[newIndex];
    };

    // Redo
    const handleRedo = () => {
        if (historyIndex >= history.length - 1) return;
        
        const newIndex = historyIndex + 1;
        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
            ctx.drawImage(img, 0, 0);
            setHistoryIndex(newIndex);
        };
        img.src = history[newIndex];
    };

    // Get mouse/touch position
    const getPosition = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        let clientX, clientY;
        if (e.touches) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }
        
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    // Start drawing
    const handleStart = (e) => {
        e.preventDefault();
        const pos = getPosition(e);
        setStartPos(pos);
        setIsDrawing(true);

        if (tool === 'text') {
            setTextPosition(pos);
            return;
        }

        if (tool === 'crop') {
            setCropStart(pos);
            setCropEnd(pos);
            setIsCropping(true);
            return;
        }

        if (tool === 'pen' || tool === 'highlight') {
            ctx.beginPath();
            ctx.moveTo(pos.x, pos.y);
            
            if (tool === 'highlight') {
                ctx.globalAlpha = 0.3;
                ctx.strokeStyle = color;
                ctx.lineWidth = brushSize * 5;
            } else {
                ctx.globalAlpha = 1;
                ctx.strokeStyle = color;
                ctx.lineWidth = brushSize;
            }
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    };

    // Drawing
    const handleMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        
        const pos = getPosition(e);

        if (tool === 'crop' && isCropping) {
            setCropEnd(pos);
            // Draw crop overlay
            redrawWithCropOverlay(pos);
            return;
        }

        if (tool === 'pen' || tool === 'highlight') {
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
        } else if (['rectangle', 'circle', 'arrow'].includes(tool)) {
            // Redraw from history for shape preview
            if (history[historyIndex]) {
                const img = new Image();
                img.onload = () => {
                    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
                    ctx.drawImage(img, 0, 0);
                    drawShape(startPos, pos, true);
                };
                img.src = history[historyIndex];
            }
        } else if (tool === 'blur') {
            applyBlur(pos);
        }
    };

    // Redraw with crop overlay
    const redrawWithCropOverlay = (endPos) => {
        if (!history[historyIndex]) return;
        
        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
            ctx.drawImage(img, 0, 0);
            
            // Draw semi-transparent overlay
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
            
            // Clear the crop area
            const x = Math.min(cropStart.x, endPos.x);
            const y = Math.min(cropStart.y, endPos.y);
            const w = Math.abs(endPos.x - cropStart.x);
            const h = Math.abs(endPos.y - cropStart.y);
            
            ctx.clearRect(x, y, w, h);
            ctx.drawImage(img, x, y, w, h, x, y, w, h);
            
            // Draw crop border
            ctx.strokeStyle = '#00BFFF';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);
        };
        img.src = history[historyIndex];
    };

    // End drawing
    const handleEnd = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        
        const pos = getPosition(e.changedTouches ? e.changedTouches[0] : e);

        if (tool === 'crop') {
            setCropEnd(pos);
            setIsDrawing(false);
            return;
        }

        if (['rectangle', 'circle', 'arrow'].includes(tool)) {
            // Restore from history first
            if (history[historyIndex]) {
                const img = new Image();
                img.onload = () => {
                    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
                    ctx.drawImage(img, 0, 0);
                    drawShape(startPos, pos, false);
                    saveToHistory();
                };
                img.src = history[historyIndex];
            }
        } else if (tool === 'pen' || tool === 'highlight') {
            ctx.globalAlpha = 1;
            saveToHistory();
        } else if (tool === 'blur') {
            saveToHistory();
        }
        
        setIsDrawing(false);
    };

    // Draw shape
    const drawShape = (start, end, isPreview = false) => {
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = brushSize;
        ctx.globalAlpha = 1;

        if (tool === 'rectangle') {
            const x = Math.min(start.x, end.x);
            const y = Math.min(start.y, end.y);
            const w = Math.abs(end.x - start.x);
            const h = Math.abs(end.y - start.y);
            ctx.strokeRect(x, y, w, h);
        } else if (tool === 'circle') {
            const centerX = (start.x + end.x) / 2;
            const centerY = (start.y + end.y) / 2;
            const radiusX = Math.abs(end.x - start.x) / 2;
            const radiusY = Math.abs(end.y - start.y) / 2;
            
            ctx.beginPath();
            ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
            ctx.stroke();
        } else if (tool === 'arrow') {
            drawArrow(start.x, start.y, end.x, end.y);
        }
    };

    // Draw arrow
    const drawArrow = (fromX, fromY, toX, toY) => {
        const headLength = 15;
        const angle = Math.atan2(toY - fromY, toX - fromX);
        
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.stroke();
        
        // Arrow head
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 6),
            toY - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(toX, toY);
        ctx.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 6),
            toY - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();
    };

    // Apply blur effect
    const applyBlur = (pos) => {
        const size = brushSize * 10;
        const x = Math.max(0, pos.x - size / 2);
        const y = Math.max(0, pos.y - size / 2);
        const w = Math.min(size, canvasSize.width - x);
        const h = Math.min(size, canvasSize.height - y);
        
        if (w <= 0 || h <= 0) return;
        
        const imageData = ctx.getImageData(x, y, w, h);
        const data = imageData.data;
        
        // Simple box blur
        const blurRadius = 5;
        for (let i = 0; i < data.length; i += 4) {
            const px = (i / 4) % w;
            const py = Math.floor((i / 4) / w);
            
            let r = 0, g = 0, b = 0, count = 0;
            
            for (let dx = -blurRadius; dx <= blurRadius; dx++) {
                for (let dy = -blurRadius; dy <= blurRadius; dy++) {
                    const nx = px + dx;
                    const ny = py + dy;
                    if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
                        const ni = (ny * w + nx) * 4;
                        r += data[ni];
                        g += data[ni + 1];
                        b += data[ni + 2];
                        count++;
                    }
                }
            }
            
            data[i] = r / count;
            data[i + 1] = g / count;
            data[i + 2] = b / count;
        }
        
        ctx.putImageData(imageData, x, y);
    };

    // Add text
    const handleAddText = () => {
        if (!textInput.trim() || !textPosition) return;
        
        ctx.font = `${brushSize * 8}px Arial`;
        ctx.fillStyle = color;
        ctx.fillText(textInput, textPosition.x, textPosition.y);
        
        setTextInput('');
        setTextPosition(null);
        saveToHistory();
    };

    // Apply crop
    const applyCrop = () => {
        if (!cropStart || !cropEnd) return;
        
        const x = Math.min(cropStart.x, cropEnd.x);
        const y = Math.min(cropStart.y, cropEnd.y);
        const w = Math.abs(cropEnd.x - cropStart.x);
        const h = Math.abs(cropEnd.y - cropStart.y);
        
        if (w < 10 || h < 10) return;
        
        const imageData = ctx.getImageData(x, y, w, h);
        
        // Resize canvas
        const canvas = canvasRef.current;
        canvas.width = w;
        canvas.height = h;
        setCanvasSize({ width: w, height: h });
        
        ctx.putImageData(imageData, 0, 0);
        
        setCropStart(null);
        setCropEnd(null);
        setIsCropping(false);
        setTool('pen');
        saveToHistory();
    };

    // Cancel crop
    const cancelCrop = () => {
        if (history[historyIndex]) {
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
                ctx.drawImage(img, 0, 0);
            };
            img.src = history[historyIndex];
        }
        setCropStart(null);
        setCropEnd(null);
        setIsCropping(false);
    };

    // Save image
    const handleSave = () => {
        const canvas = canvasRef.current;
        const dataUrl = canvas.toDataURL('image/png');
        onSave(dataUrl);
    };

    if (!imageLoaded) {
        return (
            <div className="image-editor-overlay">
                <div className="image-editor-loading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="image-editor-overlay">
            <div className="image-editor-modal">
                {/* Toolbar */}
                <div className="ie-toolbar">
                    <div className="ie-tools">
                        <button 
                            className={`ie-tool-btn ${tool === 'pen' ? 'active' : ''}`}
                            onClick={() => setTool('pen')}
                            title={t.pen}
                        >
                            🖊️
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'rectangle' ? 'active' : ''}`}
                            onClick={() => setTool('rectangle')}
                            title={t.rectangle}
                        >
                            ⬜
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'circle' ? 'active' : ''}`}
                            onClick={() => setTool('circle')}
                            title={t.circle}
                        >
                            ⭕
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'arrow' ? 'active' : ''}`}
                            onClick={() => setTool('arrow')}
                            title={t.arrow}
                        >
                            ➡️
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'text' ? 'active' : ''}`}
                            onClick={() => setTool('text')}
                            title={t.text}
                        >
                            📝
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'highlight' ? 'active' : ''}`}
                            onClick={() => setTool('highlight')}
                            title={t.highlight}
                        >
                            🖍️
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'blur' ? 'active' : ''}`}
                            onClick={() => setTool('blur')}
                            title={t.blur}
                        >
                            🔲
                        </button>
                        <button 
                            className={`ie-tool-btn ${tool === 'crop' ? 'active' : ''}`}
                            onClick={() => setTool('crop')}
                            title={t.crop}
                        >
                            ✂️
                        </button>
                    </div>

                    <div className="ie-separator"></div>

                    {/* Colors */}
                    <div className="ie-colors">
                        {colors.map(c => (
                            <button
                                key={c}
                                className={`ie-color-btn ${color === c ? 'active' : ''}`}
                                style={{ backgroundColor: c }}
                                onClick={() => setColor(c)}
                            />
                        ))}
                    </div>

                    <div className="ie-separator"></div>

                    {/* Brush Size */}
                    <div className="ie-size">
                        <span>{t.size}:</span>
                        <button 
                            className={`ie-size-btn ${brushSize === 2 ? 'active' : ''}`}
                            onClick={() => setBrushSize(2)}
                        >
                            {t.small}
                        </button>
                        <button 
                            className={`ie-size-btn ${brushSize === 4 ? 'active' : ''}`}
                            onClick={() => setBrushSize(4)}
                        >
                            {t.medium}
                        </button>
                        <button 
                            className={`ie-size-btn ${brushSize === 8 ? 'active' : ''}`}
                            onClick={() => setBrushSize(8)}
                        >
                            {t.large}
                        </button>
                    </div>

                    <div className="ie-separator"></div>

                    {/* Undo/Redo */}
                    <div className="ie-history">
                        <button 
                            className="ie-history-btn"
                            onClick={handleUndo}
                            disabled={historyIndex <= 0}
                            title={t.undo}
                        >
                            ↩️ {t.undo}
                        </button>
                        <button 
                            className="ie-history-btn"
                            onClick={handleRedo}
                            disabled={historyIndex >= history.length - 1}
                            title={t.redo}
                        >
                            ↪️ {t.redo}
                        </button>
                    </div>
                </div>

                {/* Crop controls */}
                {tool === 'crop' && cropStart && cropEnd && (
                    <div className="ie-crop-controls">
                        <button className="ie-crop-btn apply" onClick={applyCrop}>
                            ✓ {t.applyCrop}
                        </button>
                        <button className="ie-crop-btn cancel" onClick={cancelCrop}>
                            ✕ {t.cancelCrop}
                        </button>
                    </div>
                )}

                {/* Text input */}
                {tool === 'text' && textPosition && (
                    <div className="ie-text-input" style={{ top: textPosition.y, left: textPosition.x }}>
                        <input
                            type="text"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
                            placeholder={t.enterText}
                            autoFocus
                        />
                        <button onClick={handleAddText}>✓</button>
                    </div>
                )}

                {/* Canvas */}
                <div className="ie-canvas-container" ref={containerRef}>
                    <canvas
                        ref={canvasRef}
                        onMouseDown={handleStart}
                        onMouseMove={handleMove}
                        onMouseUp={handleEnd}
                        onMouseLeave={handleEnd}
                        onTouchStart={handleStart}
                        onTouchMove={handleMove}
                        onTouchEnd={handleEnd}
                        style={{ cursor: tool === 'text' ? 'text' : 'crosshair' }}
                    />
                </div>

                {/* Footer */}
                <div className="ie-footer">
                    <button className="ie-btn ie-btn-cancel" onClick={onCancel}>
                        {t.cancel}
                    </button>
                    <button className="ie-btn ie-btn-save" onClick={handleSave}>
                        {t.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageEditor;
